import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  ContentPagination,
  FormBase,
  type IBaseModalProps,
  SearchBar,
  SelectData,
  TemplatesType,
  templatesTypes,
} from '@gbs-monorepo-packages/common';
import {
  CreateCourseFromTemplateSchema,
  createCourseFromTemplateSchema,
} from '@gbs-monorepo/edge/formSchemas/courseSchema';
import { zodResolver } from '@hookform/resolvers/zod';

import { CenteredText } from '../../../../components/CenteredText';
import { FormPaginationModal } from '../../../../components/FormPaginationModal';
import { VALIDATION_LIMITS } from '../../../../constants/ValidationRules';
import {
  type IApiThrowsError,
  type IPaginationMetaProps,
} from '../../../../services/api';
import { type ICreateCourseFromTemplatesProps } from '../../../../services/courses';
import {
  type IPaginationTemplatesDTO,
  type ITemplatePageDTO,
  getTemplatePagesByClientId,
} from '../../../../services/templates';
import Logger from '../../../../utils/logger';
import {
  Content,
  CustomCheckBoxGroup,
  Loading,
  LoadingContainer,
  MainContainer,
  SelectContainer,
  SelectTemplateTypeContainer,
  TemplateCardCustom,
  Text,
  TitleText,
} from './styles';

interface IAddPageModalProps extends Partial<
  Omit<IBaseModalProps, 'children' | 'onOpenChange'>
> {
  clientId: number;
  onAccept: (
    course: Pick<ICreateCourseFromTemplatesProps, 'pageTemplateIds'>
  ) => Promise<void>;
  onDecline: () => void;
  loading?: boolean;
}
export interface ITemplatePage extends ITemplatePageDTO {
  index?: number;
  selected?: boolean;
  templateOptionsId?: number;
}
const templatesOptionsFiltered = templatesTypes.filter(
  (i) => i.key !== TemplatesType.BUILD_SCRATCH
);

const fromGlobalTemplates = TemplatesType.GLOBAL_TEMPLATES;

export const AddCourseWithTemplateModal = ({
  open = false,
  clientId,
  onAccept,
  onDecline,
  loading,
  ...props
}: IAddPageModalProps): JSX.Element => {
  const isSelectOpen = useRef(new Set());
  const [loadingCreatingCourse, setLoadingCreatingCourse] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState<Map<number, ITemplatePage>>(
    new Map()
  );
  const [search, setSearch] = useState<string | null>(null);
  const [templateOptionsId, setTemplateOptionsId] =
    useState<number>(fromGlobalTemplates);
  const refFormModalContent = useRef<HTMLDivElement>(null);
  const [paginationMeta, setPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);

  const sortedSelectedTemplates = useMemo(
    () =>
      Array.from(templates.values())
        .filter((template) => template.selected === true)
        .sort((a, b) => a.title.localeCompare(b.title)),
    [templates]
  );

  const createCourseForm = useForm<CreateCourseFromTemplateSchema>({
    resolver: zodResolver(createCourseFromTemplateSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      pageTemplateIds: [],
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
    watch,
    setValue,
    reset,
    trigger,
  } = createCourseForm;

  const values = watch();

  const checkSelectedTemplate = useCallback(
    (id: number) => {
      return values.pageTemplateIds.includes(id);
    },
    [values.pageTemplateIds]
  );

  const resetForm = useCallback(() => {
    reset({
      pageTemplateIds: [],
    });
    void trigger();
    setTemplateOptionsId(fromGlobalTemplates);
    setSearch(null);
    setTemplates(new Map());
    setPaginationMeta(null);
  }, [reset, trigger]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const searchTemplates = useCallback(
    (searchByButton = false) => {
      if (!loadingTemplates || searchByButton) {
        setLoadingTemplates(true);
        const pageAux = searchByButton
          ? 0
          : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);

        getTemplatePagesByClientId({
          clientId: clientId,
          templateOptionsId,
          page: pageAux + 1,
          limit: 10,
          filter: search ?? '',
        })
          .then((response: IPaginationTemplatesDTO) => {
            setPaginationMeta(response.meta);

            const templatesAux: Map<number, ITemplatePage> = !searchByButton
              ? new Map(templates)
              : new Map<number, ITemplatePage>();

            response.data.forEach((item) => {
              const isSelected = checkSelectedTemplate(item.id);

              const template = templatesAux.get(item.id);
              if (template) {
                templatesAux.set(item.id, {
                  ...template,
                  selected: isSelected,
                });
              } else {
                templatesAux.set(item.id, { ...item, selected: isSelected });
              }
            });

            setTemplates(templatesAux);
          })
          .catch((error: IApiThrowsError) => {
            Logger.debug('error: ', error);
          })
          .finally(() => {
            setLoadingTemplates(false);
          });
      }
    },
    [
      clientId,
      templateOptionsId,
      search,
      paginationMeta,
      loadingTemplates,
      checkSelectedTemplate,
      templates,
    ]
  );

  const searchTemplatesRef = useRef(searchTemplates);

  useEffect(() => {
    searchTemplatesRef.current = searchTemplates;
  }, [searchTemplates]);

  const handleDeclineAddPage = useCallback(() => {
    if (!isSelectOpen.current.size) {
      resetForm();
      onDecline?.();
    }
  }, [onDecline, resetForm]);

  const checkTemplate = (data: ITemplatePage) => {
    setValue('pageTemplateIds', [...values.pageTemplateIds, data.id], {
      shouldValidate: true,
    });

    setTemplates((prev) => {
      const updatedMap = new Map<number, ITemplatePage>(prev);
      if (updatedMap.has(data.id)) {
        const template = updatedMap.get(data.id);
        if (template) {
          updatedMap.set(data.id, { ...template, selected: true });
        }
      }
      return updatedMap;
    });
  };

  const removeTemplate = (data: ITemplatePage) => {
    setValue(
      'pageTemplateIds',
      values.pageTemplateIds.filter((id) => id !== data.id),
      { shouldValidate: true }
    );

    setTemplates((prevTeste) => {
      const updatedMap = new Map(prevTeste);
      if (updatedMap.has(data.id)) {
        const template = updatedMap.get(data.id);
        if (template) {
          updatedMap.set(data.id, { ...template, selected: false });
        }
      }
      return updatedMap;
    });
  };

  useEffect(() => {
    const currentSearch = search;
    const timer = setTimeout(() => {
      if (currentSearch !== null) {
        searchTemplatesRef.current(true);
      }
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    if (!open || !clientId) {
      setLoadingTemplates(false);
      return;
    }

    let cancelled = false;

    const loadInitialTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const result: IPaginationTemplatesDTO =
          await getTemplatePagesByClientId({
            clientId,
            templateOptionsId,
            page: 1,
            limit: 10,
            filter: '',
          });

        if (cancelled) {
          return;
        }

        setTemplates((prev) => {
          const templatesAux = new Map<number, ITemplatePage>(prev);
          result.data.forEach((item) => {
            if (!templatesAux.has(item.id)) {
              templatesAux.set(item.id, item);
            }
          });
          return templatesAux;
        });
        setPaginationMeta(result.meta);
      } catch (error: unknown) {
        if (!cancelled) {
          Logger.debug('error: ', error);
        }
      } finally {
        if (!cancelled) {
          setLoadingTemplates(false);
        }
      }
    };

    void loadInitialTemplates();

    return () => {
      cancelled = true;
      setLoadingTemplates(false);
    };
  }, [open, clientId, templateOptionsId]);

  const onSubmit: SubmitHandler<CreateCourseFromTemplateSchema> = useCallback(
    async (data: CreateCourseFromTemplateSchema) => {
      try {
        setLoadingCreatingCourse(true);
        await onAccept?.({ pageTemplateIds: data.pageTemplateIds });
        resetForm();
      } catch (error) {
        Logger.debug('error: ', error);
      } finally {
        setLoadingCreatingCourse(false);
      }
    },
    [onAccept, resetForm]
  );

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  return (
    <FormBase.Provider {...createCourseForm}>
      <FormPaginationModal
        {...props}
        open={open}
        acceptText="Create course"
        dataCy="title-courseTemplate"
        declineText="Cancel"
        mainText="Create course from template"
        disabled={!isValid}
        loading={loadingCreatingCourse || loading}
        onAccept={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
        vhHeight="70vh"
        onDecline={handleDeclineAddPage}
        onOpenChange={handleDeclineAddPage}
        refContent={refFormModalContent}
        biggerModal
        fullWidth
      >
        <TitleText data-cy="description-selectTemplates">
          Select the option of template you would like to use.
          {isValid && ` (${values.pageTemplateIds?.length ?? 0} selected)`}
        </TitleText>
        <SelectTemplateTypeContainer data-cy="template type">
          <SelectData
            dataCy="select-templateType"
            data={templatesOptionsFiltered}
            defaultValue={String(templateOptionsId)} // Global Template
            placeholder="Choose a template type"
            onValueChange={(id) => {
              setTemplateOptionsId(+id);
              reset({
                pageTemplateIds: [],
              });
              void trigger();
              setTemplates(new Map());
              setSearch(null);
              setPaginationMeta(null);
            }}
            onOpenChange={(isOpen) => {
              handleOpenChange(isOpen, 'template');
            }}
            required
            zIndex={10}
          />
        </SelectTemplateTypeContainer>

        {loadingTemplates && Array.from(templates.values()).length === 0 ? (
          <LoadingContainer data-cy="loading-templates-container">
            <Loading dataCy="loading-templates" />
          </LoadingContainer>
        ) : (
          <MainContainer>
            <SearchBar
              dataCy="input-searchTemplate"
              id="search-template"
              search={search ?? ''}
              maxLength={VALIDATION_LIMITS.PAGE.SEARCH_MAX}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onClearInput={() => {
                setSearch('');
              }}
              loading={loadingTemplates}
            />
            <Content>
              <ContentPagination dataCy="content-templates">
                {loadingTemplates &&
                Array.from(templates.values()).length === 0 ? (
                  <LoadingContainer data-cy="loading-templates-container">
                    <Loading dataCy="loading-templates" />
                  </LoadingContainer>
                ) : Array.from(templates.values()).length > 0 ? (
                  <InfiniteScroll
                    height={200}
                    style={{
                      overflow: 'auto',
                      maxHeight: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    dataLength={Array.from(templates.values()).length} // This is important field to render the next data
                    next={searchTemplates}
                    hasMore={
                      Array.from(templates.values()).length <
                      (paginationMeta?.total ?? 0)
                    }
                    loader={
                      <LoadingContainer data-cy="loading-templates-container">
                        <Loading />
                      </LoadingContainer>
                    }
                    endMessage={
                      <CenteredText
                        message="You have seen it all"
                        dataCy="pagination-endLimit"
                      />
                    }
                  >
                    <CustomCheckBoxGroup
                      {...props}
                      type="multiple"
                      data-cy="select-template-container"
                    >
                      {Array.from(templates.values()).map((data) => (
                        <TemplateCardCustom
                          dataCy={`template-card-${data.id}-container`}
                          data={data}
                          key={data.id}
                          disabled={data.selected === true || loadingTemplates}
                          data-state={
                            data.selected === false ||
                            data.selected === undefined
                              ? 'off'
                              : 'on'
                          }
                          onClick={() => {
                            if (
                              data.selected === false ||
                              data.selected === undefined
                            ) {
                              checkTemplate(data);
                            }
                          }}
                        />
                      ))}
                    </CustomCheckBoxGroup>
                  </InfiniteScroll>
                ) : (
                  <CenteredText
                    message="No templates found"
                    dataCy="text-noTemplates-found"
                  />
                )}
              </ContentPagination>
              <ContentPagination dataCy="content-templates">
                {sortedSelectedTemplates.length > 0 ? (
                  <SelectContainer>
                    <CustomCheckBoxGroup
                      {...props}
                      type="multiple"
                      data-cy="select-template-container"
                    >
                      {sortedSelectedTemplates.map((data) => (
                        <TemplateCardCustom
                          data={data}
                          key={data.id}
                          data-state="on"
                          onClick={() => {
                            removeTemplate(data);
                          }}
                        />
                      ))}
                    </CustomCheckBoxGroup>
                  </SelectContainer>
                ) : (
                  <Text data-cy="no-templates-selected">
                    No templates selected
                  </Text>
                )}
              </ContentPagination>
            </Content>
          </MainContainer>
        )}
      </FormPaginationModal>
    </FormBase.Provider>
  );
};
